'use client';

import { type ReactNode, useCallback, useRef, useState } from 'react';
import { useStoreApi } from '@/app/providers/store';
import { hasLiveFaceConsent, saveLiveFaceConsent } from '../_hooks/live-call-consent';
import { LiveFaceConsentModal } from './LiveFaceConsentModal';

interface UseLiveFaceConsentResult {
  /** Runs `action` directly when face consent exists (or off the China site), otherwise asks for consent first. */
  runWithFaceConsent: (action: () => void | Promise<void>) => void;
  /** Face consent dialog; callers mount it in their own JSX. */
  faceConsentModal: ReactNode;
}

/** Guards camera and portrait upload actions behind the face recognition consent. */
export function useLiveFaceConsent(): UseLiveFaceConsentResult {
  const storeApi = useStoreApi();
  const [isOpen, setIsOpen] = useState(false);
  const pendingActionRef = useRef<(() => void | Promise<void>) | null>(null);

  const runWithFaceConsent = useCallback(
    (action: () => void | Promise<void>) => {
      if (hasLiveFaceConsent(storeApi.getState().userInfo?.id)) {
        void action();
        return;
      }
      pendingActionRef.current = action;
      setIsOpen(true);
    },
    [storeApi],
  );

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      pendingActionRef.current = null;
    }
  }, []);

  const handleConfirm = useCallback(() => {
    const userId = storeApi.getState().userInfo?.id;
    if (userId) {
      saveLiveFaceConsent(userId);
    }
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (action) {
      void action();
    }
  }, [storeApi]);

  const faceConsentModal = (
    <LiveFaceConsentModal open={isOpen} onOpenChange={handleOpenChange} onConfirm={handleConfirm} />
  );

  return { runWithFaceConsent, faceConsentModal };
}
